import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SubscriptionInfo {
  name: string;
  queueLimit: number;
}

export const useProfileData = () => {
  const [companyName, setCompanyName] = useState<string>("");
  const [companyAddress, setCompanyAddress] = useState<string>("");
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/login", { replace: true });
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select(`
            company_name,
            company_address,
            subscription_status,
            subscription_tiers (
              name,
              queue_limit
            )
          `)
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;
        
        if (profile) {
          setCompanyName(profile.company_name);
          setCompanyAddress(profile.company_address || "");
          if (profile.subscription_tiers) {
            setSubscription({
              name: profile.subscription_tiers.name,
              queueLimit: profile.subscription_tiers.queue_limit
            });
          }
        }
      } catch (error: any) {
        console.error('Error fetching user data:', error);
        toast({
          title: "Error fetching user details",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, toast]);

  return { companyName, companyAddress, subscription, isLoading };
};